// A small client for the Harmovi API at api.harmovi.com. The session lives in
// an HttpOnly cookie the browser sends itself, so every request includes
// credentials and nothing here ever sees the session token.

import type {
  ClientDetail,
  ClientPage,
  ClientPatch,
  Consent,
  ConsentInput,
  Discount,
  DiscountInput,
  DisputeOutcome,
  Earnings,
  Expense,
  ExpenseInput,
  Note,
  NotePhoto,
  NoteInput,
  Package,
  PackageInput,
  PaymentMethod,
  PaymentRecord,
  Preference,
  PreferenceInput,
  Purchase,
  TimelinePage,
} from '../types/app'
import type { ForecastInputs } from '../plan/forecast'
import type { ClientNoticeData } from '../privacy/ClientNotice'

export interface MeBusiness {
  slug: string
  trading_name: string
  address_line1: string | null
  city: string | null
}

export interface Me {
  professional: {
    id: string
    email: string
    display_name: string
    timezone: string
    plan: string
    onboarding_state: string
    // When she first confirmed the notice her clients read; null until she has. Absent from an older API.
    client_notice_checked_at?: string | null
  }
  business: MeBusiness | null
  // Set while her account is closing: her data is deleted from deletes_from.
  closure?: Closure
  // Agreements she must accept before carrying on (absent from older APIs).
  legal_pending?: LegalDocument[]
  // Until when she can add packages carried over from Fresha: 60 days after
  // her Fresha import; null without one.
  fresha_carry_over_until?: string | null
  // Her plan: every lock and the plan card render from this, never from
  // what another response leaves out. Absent from an older API, which had
  // no locks.
  billing?: Billing
}

// The three rungs (ADR-025): Free, Payments, Pro. Display names live in
// brand/rungs.json; these are the identifiers the API uses.
export type Rung = 'free' | 'card_payments' | 'pro'
export type PaidRung = Exclude<Rung, 'free'>
export type BillingInterval = 'month' | 'year'
// Which lock a rung's sheet was opened from, for the funnel.
export type PromptLock = 'coach_action' | 'dream_week' | 'take_home' | 'no_show'

export interface BillingSubscription {
  interval: BillingInterval
  amount_pence: number
  current_period_end: string | null // RFC 3339
  cancel_at_period_end: boolean
  overdue: boolean // a payment failed and the subscription still entitles her
  // A founder's free months (Stripe's trial): current_period_end is the day
  // they end and her first charge. Absent from an API before 2026-10-25.
  trialing?: boolean
}

export interface Billing {
  rung: Rung
  source: 'grant' | 'subscription' | 'none'
  entitlements: { card_payments: boolean; pro: boolean }
  founder_member: boolean
  // Her Pro Checkout would start with the founder's free months: a founder
  // who has never had a subscription. Absent from an API before 2026-10-25.
  founder_trial?: boolean
  grant_until: string | null // YYYY-MM-DD, the grant's last day
  // Her grant's rung while it holds, else null: what survives a cancelled
  // subscription. Absent from an API before 2026-10-24.
  grant_rung?: PaidRung | null
  subscription: BillingSubscription | null
}

// What starting a rung did: a Stripe Checkout URL to pay on, or updated when
// her subscription was changed in place (Payments to Pro, monthly to yearly).
export interface CheckoutResult {
  url?: string
  updated?: boolean
}

export interface LegalDocument {
  document: 'terms' | 'dpa' | 'dpia'
  version: string
  title: string
  // Where she reads it: a page on harmovi.com, or a path with no host for a
  // document the app shows itself (the DPIA, from legalDocument below).
  url: string
}

// LegalText is a document the app shows itself: its text as HTML, built from
// the draft by site/build-legal.mjs and served by the API to signed-in
// professionals only.
export interface LegalText extends Omit<LegalDocument, 'url'> {
  html: string
}

export interface Closure {
  closing_at: string | null
  deletes_from: string | null
}

export type TaxRegion = 'scotland' | 'rest_of_uk'

// What online bookings pay up front (spec 2026-10-09 §1.6).
export type DepositPolicy = 'none' | 'fixed' | 'percentage' | 'full'
// Where her pay links offer Klarna, when she has turned it on.
export type KlarnaScope = 'packages' | 'appointments' | 'both'

// A client whose records have outlived her retention period
// (GET /v1/retention). Nothing is erased until she confirms.
export interface ExpiredRecord {
  client_id: string
  first_name: string
  last_name: string | null
  last_activity: string
}

export interface RetentionReview {
  retention_years: number
  cutoff: string
  clients: ExpiredRecord[]
}

export type LogoShape = 'round' | 'square'

export interface Business {
  slug: string
  trading_name: string
  description: string | null
  phone: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  postcode: string | null
  country: string
  cancellation_notice_hours: number
  record_retention_years: number
  tax_region: TaxRegion | null
  published: boolean
  show_full_address: boolean
  public_area: string | null
  deposit_policy: DepositPolicy
  deposit_amount_pence: number | null // fixed policy only, at least 100
  deposit_percentage: number | null // percentage policy only, 1 to 100
  deposits_active: boolean // read-only: a policy is set and card payments are ready
  // Her Klarna rule: off until she turns it on; then on packages, appointments
  // or both, from klarna_min_pence when set (null: any amount).
  klarna_on: boolean
  klarna_scope: KlarnaScope
  klarna_min_pence: number | null
  cover_photo_url: string | null // read-only; set with uploadPhoto
  profile_photo_url: string | null
  // The point of the cover that stays in view, as percentages across and
  // down (CSS object-position); 50/50 is the middle. A new cover goes back to
  // the middle. cover_zoom: how far she has zoomed in on it, from 100 (none)
  // to 300. A new cover goes back to none. Absent from an older API.
  cover_position_x?: number
  cover_position_y?: number
  cover_zoom?: number
  // Imported bookings get the reminders and review request too; off until her
  // old system has stopped emailing those clients itself. has_imports: she has
  // imported something, so Settings offers the switch. Absent from an older API.
  imported_reminders?: boolean
  has_imports?: boolean
  // How her booking page looks (booking/theme.ts): its colour theme, and
  // whether her logo shows round or as a rounded square. Absent from an
  // older API: Blush and round.
  page_theme?: string
  logo_shape?: LogoShape
  // Her own words on every client confirmation email; null when she has none.
  // Absent from an older API.
  confirmation_note?: string | null
  // When clients can book: the notice she needs in hours, and how far ahead in
  // days. Absent from an older API: 2 hours and 60 days.
  booking_notice_hours?: number
  booking_horizon_days?: number
  // Moves on with every save. Send it back as If-Match on PATCH /v1/business;
  // a 412 means another device saved first. Absent from an older API.
  version?: number
  // Her booking link. Until the page is first switched on (slug_chosen
  // false) the slug follows the business name; after that it changes only
  // when she sends one, and the old link keeps working. Absent from an older API.
  slug_chosen?: boolean
  booking_url?: string // e.g. https://book.harmovi.com/jessica-lopes-massage
}

export type PhotoKind = 'cover' | 'profile'

export type BusinessPatch = Partial<{
  trading_name: string
  description: string | null
  phone: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  postcode: string | null
  cancellation_notice_hours: number
  tax_region: TaxRegion
  published: boolean
  show_full_address: boolean
  public_area: string | null
  deposit_policy: DepositPolicy
  deposit_amount_pence: number | null
  deposit_percentage: number | null
  klarna_on: boolean
  klarna_scope: KlarnaScope
  klarna_min_pence: number | null
  cover_position_x: number // 0 to 100
  cover_position_y: number
  cover_zoom: number // 100 to 300
  imported_reminders: boolean
  page_theme: string // one of booking/theme.ts THEMES; 422 otherwise
  logo_shape: LogoShape
  confirmation_note: string | null // up to 500 characters; blank or null clears it
  booking_notice_hours: number // 2, 4, 12, 24, 48, 72 or 168; 422 otherwise
  booking_horizon_days: number // 14, 28, 60, 90 or 180; 422 otherwise
  // 3–60 of a-z, 0-9 and single hyphens, not reserved: 422 slug; another
  // business's (now or before): 409 slug-taken.
  slug: string
}>

// A built-in category, or the id (cat_…) of one of her own.
export type Category = 'drainage' | 'relax' | 'consult' | (string & {})

export interface ServiceCategory {
  value: Category
  label: string
  colour: string
  custom: boolean
}

// ServiceLength is one way a service can be booked: 60 min for £99.
export interface ServiceLength {
  id: string
  duration_minutes: number
  price_pence: number
}

export interface Service {
  id: string
  name: string
  description: string | null
  lengths: ServiceLength[] // shortest first, never empty
  buffer_after_minutes: number
  category: Category
  colour: string
  bookable_online: boolean
  sort_order: number
  version?: number // If-Match on PATCH /v1/services/{id}; absent from an older API
  form_template_id: string | null // the consultation form this service asks for
}

export interface ServiceInput {
  name?: string
  description?: string | null
  // The full list: an entry with an id is updated, one without is created,
  // and a saved length left out is removed.
  lengths?: { id?: string; duration_minutes: number; price_pence: number }[]
  buffer_after_minutes?: number
  category?: Category
  bookable_online?: boolean
  form_template_id?: string | null // '' or null clears it
}

// OpeningWindow is a bookable stretch of one weekday, in minutes from
// midnight. Weekday 0 is Monday; a break is the gap between two windows.
export interface OpeningWindow {
  weekday: number
  start_minute: number
  end_minute: number
}

export interface TimeOff {
  id: string
  name: string | null
  starts_on: string
  ends_on: string
}

export interface TimeOffInput {
  name: string | null
  starts_on: string
  ends_on: string
}

// TimeBlock is part of a day she has blocked off ("Dentist"), in minutes from
// midnight. It stays within one day; the label is hers alone and is null when
// she gave none.
export interface TimeBlock {
  id: string
  label: string | null
  date: string
  start_minute: number
  end_minute: number
}

// TimeBlockInput is a block to save. The app makes the id itself so a retried
// save can't block twice. force saves it over a booking or another block (the
// 409 names them first).
export interface TimeBlockInput {
  id?: string
  label: string | null
  date: string
  start_minute: number
  end_minute: number
  force?: boolean
}

export type AppointmentStatus = 'booked' | 'completed' | 'cancelled' | 'no_show'

// PaymentStatus is derived by the API: paid, part paid (a deposit taken,
// the balance still owed), completed but unpaid, refunded, or none.
export type PaymentStatus = 'none' | 'paid' | 'part_paid' | 'outstanding' | 'refunded'

// PaymentState is the one payment label every screen shows (review
// 2026-10-14): a package session pays for it; everything paid went back, or
// some of it; paid in full; completed with something still to take; part paid
// (a deposit) with the rest not due yet, or kept on a no-show or late cancel;
// a completed visit an import brought across, paid in the old system;
// nothing paid and nothing due yet.
export type PaymentState = 'package' | 'dispute_lost' | 'refunded' | 'part_refunded' | 'paid' | 'to_pay' | 'deposit_paid' | 'paid_before' | 'none'

// PaidMethod is how the newest payment was taken: card is her own reader,
// pay_link is a card payment through Harmovi (a pay link or an online deposit).
export type PaidMethod = 'cash' | 'card' | 'pay_link' | 'bank_transfer' | 'package'

export interface ClientSummary {
  id: string
  first_name: string
  last_name: string | null
  phone: string | null
}

// A new client needs every field (422 naming each one missing). The API
// stores the email trimmed and lower-cased.
export interface ClientInput {
  first_name: string
  last_name: string
  phone: string
  email: string
}

// Appointment times are the business's local wall clock: date plus minutes
// from midnight. starts_at and ends_at are the same instants in UTC.
export interface Appointment {
  id: string
  version: number
  status: AppointmentStatus
  date: string
  start_minute: number
  end_minute: number
  starts_at: string
  ends_at: string
  blocked_until: string
  blocked_until_minute: number
  client: { id: string; first_name: string; last_name: string | null } | null
  service: {
    id: string | null
    service_length_id: string | null // the length booked; null only on an item that never had one
    name: string
    duration_minutes: number
    buffer_after_minutes: number
    price_pence: number
    category: Category | null
    colour: string | null
  }
  total_price_pence: number
  payment_status: PaymentStatus
  cancellation_reason: string | null
  completed_at: string | null
  outside_hours: boolean
  refunded_pence: number
  paid_twice: boolean
  // An open dispute on one of its payments; dispute_outcome is a closed
  // one's result, lost first when there are both (absent from older APIs).
  disputed: boolean
  dispute_outcome?: DisputeOutcome
  card_checkout: CardCheckout | null
  card_payment_id: string | null
  // The payment the Refund action refunds (the latest of any method with
  // something left), and what it can still take: its amount less its pending
  // and succeeded refunds (not the appointment's whole paid total).
  refund_payment_id: string | null
  refundable_pence: number
  // The money history, oldest first; only a single appointment's read has it.
  payments: PaymentRecord[]
  // Computed by the server (spec 1.3): a saved card can be charged for what is owed.
  can_charge_card: boolean
  // Her Complete step may offer the saved card; the charge completes the visit first.
  can_charge_card_on_complete: boolean
  client_has_card: boolean
  source: 'professional' | 'booking_page' | 'import'
  booked_as_name: string | null
  contact_email: string | null // the email typed on the booking page; hers alone
  deposit_pence: number | null // the deposit this booking was made under; null when none
  paid_pence: number // taken so far, deposit included
  owed_pence: number // what is still to pay
  awaiting_deposit: boolean // a live hold: the client has not paid the deposit yet
  hold_expires_at: string | null // UTC instant the hold lapses; set only while awaiting
  // Review fixes (2026-10-14). Optional: an API from before them omits them.
  payment_state?: PaymentState
  paid_method?: PaidMethod | null
  amount_due_pence?: number // what Complete still has to take; 0 means complete with no method
  // What cancelling now does with the card deposit she holds: refunded, or
  // kept (inside her cancellation notice; send refund_deposit to refund it).
  deposit_on_cancel?: 'refund' | 'keep' | null
  deposit_on_cancel_pence?: number
  package?: { purchase_id: string; name: string } | null // the package a session of which pays for it
  // A single appointment's read only: booked, no package on it, and the
  // client has a free package session that covers it (linkPackage would work).
  package_available?: boolean
  no_show_at?: string | null
  discount: AppointmentDiscount | null // what the total was made with; total_price_pence is already net
  // The client's status for the form of this service; null when the service
  // has no form or there is no client.
  form_status: AppointmentFormStatus | null
  // When the client last signed that form (UTC instant); null if never.
  form_signed_at: string | null
}

// AppointmentDiscount is the discount an appointment carries. pence is the
// amount taken off; was_pence is the price before it; label is the sale or
// code name (or her note on a one-off), else null.
export interface AppointmentDiscount {
  source: 'manual' | 'sale' | 'code'
  kind: 'percent' | 'amount'
  value: number
  pence: number
  label: string | null
  was_pence: number
}

// DiscountRequest is what a one-off discount, or a code, looks like on the wire.
export type DiscountRequest = { discount: { kind: 'percent' | 'amount'; value: number; note?: string } } | { code: string }

export interface AppointmentEmail {
  kind: 'confirmation' | 'reminder' | 'rescheduled' | 'cancelled' | 'review_request' | 'deposit_request' | 'refund'
  status: 'pending' | 'sent' | 'failed' | 'skipped'
  sent_at: string | null
}

// What a notification opens: at most one of these is set. update opens the
// updates sheet itself (openTarget leaves it alone).
export interface NotificationTarget {
  appointment?: string
  client?: string
  client_forms?: string
  reviews?: boolean
  update?: string
}

// One line in her notification list. new is true while it is after the last
// time she opened the list.
export interface AppNotification {
  kind: string
  at: string
  new: boolean
  title: string
  detail: string
  body?: string
  target: NotificationTarget | null
}

export interface Review {
  id: string
  source: 'harmovi' | 'imported'
  source_platform: ReviewPlatform | null
  source_platform_label: string | null
  appointment_id: string | null
  client_id: string | null
  display_name: string
  rating: number | null
  body: string | null
  service_name: string | null
  reviewed_on: string
  hidden_reason: HideReason | null
  hidden_at: string | null
  reply: string | null
  replied_at: string | null
  created_at: string
}
export type ReviewPlatform = 'fresha' | 'google' | 'treatwell' | 'booksy' | 'other'
export type HideReason = 'personal_info' | 'abusive' | 'off_topic'
export interface ImportReviewInput {
  source_platform: ReviewPlatform
  source_platform_label: string | null
  display_name: string
  rating: number | null
  body: string | null
  reviewed_on: string
  service_name: string | null
  genuine: boolean
}
export type ReviewPatch = Partial<{
  reply: string | null
  hidden_reason: HideReason | null
  display_name: string
  rating: number | null
  body: string | null
  reviewed_on: string
  service_name: string | null
}>

export interface CardAccount {
  // available is false while card payments are not configured on the server
  // (an API from before this field also reads as false); the app then shows
  // nothing card-related.
  available: boolean
  status: 'none' | 'onboarding' | 'in_review' | 'active' | 'restricted'
  enabled: boolean
  ready: boolean
  // needs_attention is whether Stripe wants more information. It never stops
  // payments by itself; ready does.
  needs_attention: boolean
}

export interface CardCheckout {
  id: string
  kind: 'appointment' | 'package'
  status: 'open' | 'paid' | 'expired' | 'cancelled'
  url: string
  amount_pence: number
  expires_at: string
  payment_id: string | null
  // Whether paying this link saves the client's card.
  save_card: boolean
}

// CardCharge is a charge to a client's saved card. pending is settled by the
// webhook; needs_client means the bank wants the client to approve it.
export interface CardCharge {
  id: string
  status: 'pending' | 'succeeded' | 'failed' | 'needs_client'
  amount_pence: number
  failure: 'needs_client' | 'declined' | 'expired_card' | 'no_answer' | null
  payment_id: string | null
}

export interface RefundResult {
  id: string
  status: 'pending' | 'succeeded' | 'failed'
  amount_pence: number
  credits_removed: number
}

export type PriorityType = 'package' | 'rebooking' | 'empty_slot' | 'coach' | 'review'

export interface TodayMetrics {
  revenue_pence: number
  appointment_count: number
  free_slots: number
  packages_due: number
}

// A priority carries the reason it exists, which ADR-007 requires, and the
// number that makes it worth doing. phone and draft are set on rebooking
// priorities only: the app builds the wa.me link from them, and tapping it is
// what sends — never the server.
export interface TodayPriority {
  type: PriorityType
  title: string
  // reason and action are absent or empty off Pro, where each card is a hint:
  // type, key, title and pounds only (2026-10-24). Pro always sends both.
  reason?: string
  impact_pence: number
  action?: string
  client_id?: string
  review_id?: string
  phone?: string
  draft?: string
  // Names this card for the day: "<date>:<type>:<n>". Echoed back when she
  // acts on it or snoozes it; absent from an older API.
  key?: string
}

// greeting follows her local clock ("Good morning" / "Good afternoon" /
// "Good evening", "Hello" overnight) and is always set by an API from
// 2026-10-14 on; an older API may send "". narrative is empty when the model
// could not be reached. That is ADR-016's required degradation: the numbers
// are ours, so an outage costs the wording rather than the briefing.
export interface TodayScreen {
  date: string
  greeting: string
  narrative: string
  metrics: TodayMetrics
  priorities: TodayPriority[]
  appointments: Appointment[]
  // Clients' birthdays from today to six days ahead, soonest first. Absent
  // from an older API.
  birthdays?: TodayBirthday[]
  // How many of today's booked appointments still need the client's form.
  // Absent from an older API.
  forms_needed?: number
  // Names the narrative for her verdict on it; absent from an older API.
  narrative_key?: string
  // What she said of this narrative, null until she answers; absent from an older API.
  narrative_verdict?: SuggestionVerdict | null
}

// Her answer to "Was this helpful?" under a Coach line or the briefing, and
// on "Not really" the one fixed reason she tapped.
export type SuggestionVerdict = 'helpful' | 'not_helpful'
export type VerdictReason = 'already_knew' | 'not_my_business' | 'wrong_numbers'

export interface TodayBirthday {
  client_id: string
  first_name: string
  last_name: string | null
  phone: string | null
  date: string // this occurrence, YYYY-MM-DD
  days_away: number // 0 for today
}

export type BusinessProfile = 'full_time' | 'part_time' | 'parent_friendly' | 'side_business' | 'starting'

export interface PlanGoal {
  take_home_target_pence: number
  monthly_costs_pence: number
  holiday_weeks_per_year: number
  business_profile: BusinessProfile
  effective_from: string
}

// normalizePlanGoal fills in holiday_weeks_per_year from the old
// holiday_days_per_year field when an API response (or a cached one) only
// carries the old name. Never leaves NaN behind.
function normalizePlanGoal(goal: PlanGoal | null): PlanGoal | null {
  if (!goal) return goal
  const raw = goal as PlanGoal & { holiday_days_per_year?: number }
  if (raw.holiday_weeks_per_year == null && raw.holiday_days_per_year != null) {
    return { ...goal, holiday_weeks_per_year: Math.round(raw.holiday_days_per_year / 5) }
  }
  return goal
}

// Plan is what the planner opens on. Before setup, goal is null and windows
// are her working hours, so she starts from her real week.
export interface Plan {
  goal: PlanGoal | null
  windows: OpeningWindow[]
  inputs: ForecastInputs
  live: boolean
}

export interface PlanInput {
  take_home_target_pence: number
  monthly_costs_pence: number
  holiday_weeks_per_year: number
  business_profile: BusinessProfile
  windows: OpeningWindow[]
}

export interface CoachProgress {
  target_takings_pence: number
  so_far_pence: number
  projected_pence: number
  gap_pence: number
}

export interface CoachAction {
  id: string
  title: string
  why: string
  impact_pence: number
  hours_freed?: number
  // Names the action for her verdict on it; absent from an older API.
  key?: string
  // What she said of this action, null until she answers; absent from an older API.
  verdict?: SuggestionVerdict | null
}

// Coach is the month against her plan. Before her diary lives in Harmovi,
// started is false and there is no progress: an empty month there is not a
// bad month.
export interface Coach {
  has_goal: boolean
  started: boolean
  live_from: string | null
  progress: CoachProgress | null
  summary: string
  // Names the summary for her verdict on it; absent from an older API.
  summary_key?: string
  // What she said of this summary, null until she answers; absent from an older API.
  summary_verdict?: SuggestionVerdict | null
  actions: CoachAction[]
  generated: boolean
}

export interface AppointmentInput {
  id: string
  client_id: string
  service_length_id: string
  date: string
  start_minute: number
  force?: boolean
  discount?: { kind: 'percent' | 'amount'; value: number; note?: string }
  code?: string
  no_sale?: boolean
}

export interface AppointmentPatch {
  date?: string
  start_minute?: number
  service_length_id?: string
  force?: boolean
}

// ApiError is a response the API sent back: status plus the problem type slug,
// e.g. 401 "invalid-login-token".
// --- Consultation forms (spec 2026-10-12) ---

export type FormFieldType =
  | 'heading'
  | 'text'
  | 'short'
  | 'long'
  | 'yes_no'
  | 'checklist'
  | 'choice'
  | 'date'
  | 'statement'

export interface FormOption {
  id: string
  label: string
}

// One question or block of a form. Ids are stable across versions of a
// template when the field is kept; a new field may be sent with no id.
export interface FormField {
  id: string
  type: FormFieldType
  label: string
  required: boolean
  health: boolean
  details: boolean // yes_no: ask for details on a yes
  none_option: boolean // checklist: offer "None of these"
  options: FormOption[] // checklist and choice only
}

// An answer by field type: short, long, date and choice are a string (choice
// holds the option id), statement is true, yes_no is {value, details?}, and a
// checklist is {ticked, none}.
export interface YesNoAnswer {
  value: boolean | null
  details?: string
}
export interface ChecklistAnswer {
  ticked: string[]
  none: boolean
}
export type FormAnswer = string | boolean | YesNoAnswer | ChecklistAnswer
export type FormAnswers = Record<string, FormAnswer>

export interface FormTemplate {
  id: string
  name: string
  current_version_id: string | null
  current_version: number | null
  drawn_signature: boolean
  guardian_under_18: boolean
  archived_at: string | null
  fields: FormField[]
  service_ids: string[]
}

// A template as submitted. starter is read on create only (a key from
// listFormStarters), ask_earlier_signers on update only.
export interface FormTemplateInput {
  name?: string
  drawn_signature?: boolean
  guardian_under_18?: boolean
  fields?: Partial<FormField>[]
  starter?: string
  ask_earlier_signers?: boolean
}

export interface FormStarter {
  key: string
  name: string
  fields: FormField[]
}

export type FormFrequency = 'first_visit' | 'every_booking' | 'months'

// null and 'none' both mean there is nothing to show: no form, or none asked.
export type ClientFormStatus = 'needed' | 'updated' | 'signed' | 'none' | null
export type AppointmentFormStatus = 'needed' | 'updated' | 'signed' | 'none'

export interface FormSubmissionSummary {
  id: string
  version: number
  signed_at: string
}

// One template the client has been asked to fill in (the profile's Forms
// section). Submissions are newest first.
export interface ClientForm {
  template_id: string
  template_name: string
  status: ClientFormStatus
  submissions: FormSubmissionSummary[]
}

export interface HealthAnswer {
  field_id: string
  label: string
  value: string
}

// A submission as it was signed: the version's fields as the client saw
// them. Its drawn signature, when has_signature, is at formSignatureUrl.
export interface FormSubmission {
  id: string
  template_name: string
  version: number
  fields: FormField[]
  answers: FormAnswers
  health: HealthAnswer[]
  signed_name: string
  has_signature: boolean
  typed_only: boolean
  guardian_name: string | null
  guardian_relationship: string | null
  sent_to: string | null // the address the link went to, or "in_person"
  content_hash: string
  signed_at: string
}

// The form a client fills in. The consent wording is not part of it: it is
// CONSENT_WORDING.records, and the submit sends its version.
export interface PublicForm {
  business_name: string
  client_first: string
  template_name: string
  version_id: string
  fields: FormField[]
  drawn_signature: boolean
  guardian_under_18: boolean
  prefill: FormAnswers
  state: 'open' | 'signed' | 'closed'
}

// The submit, on the client's link. signature is
// the drawn PNG as base64 or a data URL; it is ignored when typed_only.
export interface FormSubmitInput {
  version_id: string
  answers: FormAnswers
  consent_ticked: boolean
  consent_version: string
  signed_name: string
  signature?: string
  typed_only: boolean
  guardian_name?: string | null
  guardian_relationship?: string | null
}

export class ApiError extends Error {
  // reason and detail come with 422 discount-code: why, and the server's words.
  constructor(readonly status: number, readonly problem: string, readonly reason?: string, readonly detail?: string) {
    super(`API ${status} ${problem}`)
    this.name = 'ApiError'
  }
}

// ValidationError is a 422: one message per field that needs attention.
export class ValidationError extends ApiError {
  constructor(readonly fields: Record<string, string>) {
    super(422, 'validation-failed')
    this.name = 'ValidationError'
  }
}

// EmailInUseError is a 409 email-in-use: another client of hers already has
// that email, and clientName says who.
export class EmailInUseError extends ApiError {
  constructor(readonly clientId: string, readonly clientName: string) {
    super(409, 'email-in-use')
    this.name = 'EmailInUseError'
  }
}

// ConflictError is a 409 for a booking or block that would overlap others;
// conflicts and blocks let the app name them and offer "Book anyway" or
// "Block anyway".
export class ConflictError extends ApiError {
  constructor(readonly conflicts: Appointment[], readonly blocks: TimeBlock[] = []) {
    super(409, 'appointment-conflict')
    this.name = 'ConflictError'
  }
}

// VersionMismatchError is a 412: the booking changed on another device.
export class VersionMismatchError extends ApiError {
  constructor() {
    super(412, 'version-mismatch')
    this.name = 'VersionMismatchError'
  }
}

// OutsideHoursError is a 409 from "Use as my booking hours": these bookings would
// fall outside the new hours. Nothing changed; applyPlan(true) applies the week
// and leaves every booking exactly where it is.
export class OutsideHoursError extends ApiError {
  constructor(readonly appointments: Appointment[]) {
    super(409, 'outside-new-hours')
    this.name = 'OutsideHoursError'
  }
}

// NetworkError means the API could not be reached at all.
export class NetworkError extends Error {
  constructor() {
    super('Could not reach the Harmovi API')
    this.name = 'NetworkError'
  }
}

// APP_BUILD is the commit this app was built from (GITHUB_SHA in CI, cut to
// 12 characters), or "dev" for a local build. See vite.config.ts.
export const APP_BUILD: string = __APP_BUILD__

export const API_BASE: string =
  import.meta.env.VITE_API_BASE ??
  (import.meta.env.DEV ? 'http://localhost:8080' : 'https://api.harmovi.com')

type Fetch = typeof fetch

// The POSTs the API refuses without an Idempotency-Key: completing, recording
// a payment, a pay link, a package sale and a refund (docs/22, "Idempotency").
const IDEMPOTENT_POSTS = [
  /^\/v1\/appointments\/[^/]+\/(complete|payments)$/,
  /^\/v1\/payments\/checkouts$/,
  /^\/v1\/payments\/charges$/,
  /^\/v1\/clients\/[^/]+\/packages$/,
  /^\/v1\/payments\/[^/]+\/refunds$/,
]

export function needsIdempotencyKey(path: string): boolean {
  return IDEMPOTENT_POSTS.some((re) => re.test(path))
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

// build, when given, is the app's build, which every request names in Accept
// ("application/json; harmovi-build=<commit>") so the API can see which
// builds are still in use. Accept is a header browsers send without a CORS
// preflight; a custom header would add one to every GET (architecture review
// 2026-10-25, W4).
export function createApi(base: string, fetchImpl: Fetch = (...args) => fetch(...args), { build }: { build?: string } = {}) {
  const accept = build ? { Accept: `application/json; harmovi-build=${build}` } : undefined
  // The PWA and API deploy in parallel, so an old API response can omit the
  // blocked-time fields entirely. Default them here, once, so every caller
  // gets the non-optional shape the types promise: no buffer means no extra
  // time, and no blocked-until means blocked time ends where the booking does.
  // An old API response can omit tax_region entirely, before the field
  // existed; normalise that to null rather than leaving it undefined.
  const normalizeBusiness = (b: Business): Business => ({ ...b, tax_region: b.tax_region ?? null, published: b.published ?? false, show_full_address: b.show_full_address ?? false, public_area: b.public_area ?? null, deposit_policy: b.deposit_policy ?? 'none', deposit_amount_pence: b.deposit_amount_pence ?? null, deposit_percentage: b.deposit_percentage ?? null, deposits_active: b.deposits_active ?? false, klarna_on: b.klarna_on ?? false, klarna_scope: b.klarna_scope ?? 'packages', klarna_min_pence: b.klarna_min_pence ?? null, cover_photo_url: b.cover_photo_url ?? null, profile_photo_url: b.profile_photo_url ?? null })
  const normalizeService = (s: Service): Service => ({ ...s, buffer_after_minutes: s.buffer_after_minutes ?? 0, form_template_id: s.form_template_id ?? null })
  const normalizeAppointment = (a: Appointment): Appointment => ({
    ...a,
    blocked_until: a.blocked_until ?? a.ends_at,
    blocked_until_minute: a.blocked_until_minute ?? a.end_minute,
    service: { ...a.service, service_length_id: a.service.service_length_id ?? null, buffer_after_minutes: a.service.buffer_after_minutes ?? 0 },
    refunded_pence: a.refunded_pence ?? 0,
    paid_twice: a.paid_twice ?? false,
    disputed: a.disputed ?? false,
    dispute_outcome: a.dispute_outcome ?? null,
    card_checkout: a.card_checkout ?? null,
    card_payment_id: a.card_payment_id ?? null,
    // An older API names only the card payment and sends no per-payment
    // figure; what was paid less refunds is the nearest it has.
    refund_payment_id: a.refund_payment_id ?? a.card_payment_id ?? null,
    refundable_pence:
      a.refundable_pence ??
      Math.max(0, (a.paid_pence > 0 ? Math.min(a.paid_pence, a.total_price_pence) : a.total_price_pence) - (a.refunded_pence ?? 0)),
    payments: a.payments ?? [],
    can_charge_card: a.can_charge_card ?? false,
    can_charge_card_on_complete: a.can_charge_card_on_complete ?? false,
    client_has_card: a.client_has_card ?? false,
    source: a.source ?? 'professional',
    booked_as_name: a.booked_as_name ?? null,
    contact_email: a.contact_email ?? null,
    // An API from before deposits omits these. Nothing is paid then, so the
    // whole price is owed unless the booking is settled or refunded.
    deposit_pence: a.deposit_pence ?? null,
    paid_pence: a.paid_pence ?? 0,
    owed_pence: a.owed_pence ?? (a.payment_status === 'paid' || a.payment_status === 'refunded' ? 0 : a.total_price_pence),
    awaiting_deposit: a.awaiting_deposit ?? false,
    hold_expires_at: a.hold_expires_at ?? null,
    discount: a.discount ?? null,
    form_status: a.form_status ?? null,
    form_signed_at: a.form_signed_at ?? null,
  })
  // Card-payment fields are newer than the API that serves some responses.
  const normalizePurchase = (p: Purchase): Purchase => ({
    ...p,
    paid_by_card: p.paid_by_card ?? false,
    payment_id: p.payment_id ?? null,
    refunded_pence: p.refunded_pence ?? 0,
    refunded_outside: p.refunded_outside ?? false,
    refundable_pence: p.refundable_pence ?? Math.max(0, (p.price_paid_pence ?? 0) - (p.refunded_pence ?? 0)),
    payments: p.payments ?? [],
    disputed: p.disputed ?? false,
    dispute_outcome: p.dispute_outcome ?? null,
  })
  const normalizeEarnings = (e: Earnings): Earnings => ({ ...e, refunds_pence: e.refunds_pence ?? 0, disputes_pence: e.disputes_pence ?? 0, discounts_pence: e.discounts_pence ?? 0 })
  const normalizeCardAccount = (a: CardAccount): CardAccount => ({
    ...a,
    available: a.available ?? false,
    needs_attention: a.needs_attention ?? false,
  })

  // Told about any 401 outside sign-in itself: the session has ended (signed
  // out on another device, or expired), and the app shows one "signed out"
  // state instead of letting each screen fail on its own.
  let unauthorized: (() => void) | null = null

  async function request(path: string, init: RequestInit = {}): Promise<Response> {
    let res: Response
    try {
      const headers = accept ? { ...accept, ...(init.headers as Record<string, string> | undefined) } : init.headers
      res = await fetchImpl(base + path, { ...init, ...(headers ? { headers } : {}), credentials: 'include' })
    } catch {
      throw new NetworkError()
    }
    if (res.status === 401 && !path.startsWith('/auth/')) {
      unauthorized?.()
    }
    if (!res.ok) {
      let body: { type?: unknown; errors?: unknown; conflicts?: unknown; blocks?: unknown; appointments?: unknown; client_id?: unknown; client_name?: unknown; reason?: unknown; detail?: unknown } | null = null
      try {
        body = await res.json()
      } catch {
        // Not a problem+json body; the status alone will have to do.
      }
      const errors = body?.errors
      if (res.status === 422 && Array.isArray(errors)) {
        const fields: Record<string, string> = {}
        for (const e of errors as { field?: unknown; message?: unknown }[]) {
          if (typeof e?.field === 'string' && !(e.field in fields)) {
            fields[e.field] = String(e.message ?? '')
          }
        }
        throw new ValidationError(fields)
      }
      const type = body?.type
      const problem = typeof type === 'string' ? type.split('/').pop() || 'unknown' : 'unknown'
      if (res.status === 409 && problem === 'appointment-conflict') {
        const conflicts = Array.isArray(body?.conflicts) ? (body.conflicts as Appointment[]).map(normalizeAppointment) : []
        // An API from before time blocks sends no blocks.
        const blocks = Array.isArray(body?.blocks) ? (body.blocks as TimeBlock[]) : []
        throw new ConflictError(conflicts, blocks)
      }
      if (res.status === 409 && problem === 'outside-new-hours') {
        const appointments = Array.isArray(body?.appointments)
          ? (body.appointments as Appointment[]).map(normalizeAppointment)
          : []
        throw new OutsideHoursError(appointments)
      }
      if (res.status === 409 && problem === 'email-in-use') {
        throw new EmailInUseError(String(body?.client_id ?? ''), String(body?.client_name ?? ''))
      }
      if (res.status === 412) {
        throw new VersionMismatchError()
      }
      throw new ApiError(
        res.status,
        problem,
        typeof body?.reason === 'string' ? body.reason : undefined,
        typeof body?.detail === 'string' ? body.detail : undefined,
      )
    }
    return res
  }

  async function get<T>(path: string): Promise<T> {
    const res = await request(path)
    return res.json()
  }

  async function send<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
    const init: RequestInit = { method }
    const allHeaders: Record<string, string> = { ...headers }
    // The API requires an Idempotency-Key on the POSTs that move money or
    // sell (428 idempotency-key-required otherwise). A caller holding one key
    // for a whole sheet passes it in headers; otherwise this call gets its
    // own, which the request below reuses if it is retried.
    if (method === 'POST' && needsIdempotencyKey(path) && !allHeaders['Idempotency-Key']) {
      allHeaders['Idempotency-Key'] = newIdempotencyKey()
    }
    if (body !== undefined) {
      allHeaders['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    } else if (method === 'POST') {
      // The API refuses an untyped POST: with no type it would be
      // CORS-simple, so it would skip the preflight (review 2026-10-19 L1).
      allHeaders['Content-Type'] = 'application/json'
    }
    if (Object.keys(allHeaders).length > 0) {
      init.headers = allHeaders
    }
    const res = await request(path, init)
    return (res.status === 204 ? undefined : await res.json()) as T
  }

  const id = (value: string) => encodeURIComponent(value)

  const ifMatch = (version: number) => ({ 'If-Match': `"${version}"` })
  // An older API sends no version; the save then goes without the check.
  const maybeIfMatch = (version?: number): Record<string, string> => (typeof version === 'number' ? ifMatch(version) : {})

  // Versions this device's own saves produced, per resource. Several parts of
  // one screen can save the same business (photos, about you, the booking
  // page switch, the business form): each holds the version it loaded, and
  // a save by a neighbour on this device must not look like "changed on
  // another device". Versions go up by one per save, so a form's version is
  // moved past every later version this device made, and stops at the first
  // one it didn't (another device's), which then answers 412.
  const ownVersions = new Map<string, Set<number>>()
  const rebase = (resource: string, version?: number): number | undefined => {
    if (typeof version !== 'number') return version
    const own = ownVersions.get(resource)
    let v = version
    while (own?.has(v + 1)) v++
    return v
  }
  const remember = <T extends { version?: number }>(resource: string) => (saved: T): T => {
    if (typeof saved.version === 'number') {
      const own = ownVersions.get(resource) ?? new Set<number>()
      own.add(saved.version)
      ownVersions.set(resource, own)
    }
    return saved
  }

  // compareClients orders search results by last name then first name,
  // case-insensitively, so searchClients can restore the alphabetical order
  // the booking sheet expects.
  const compareClients = (a: ClientSummary, b: ClientSummary): number => {
    const collate = (x: string, y: string) => x.localeCompare(y, undefined, { sensitivity: 'base' })
    return collate(a.last_name ?? '', b.last_name ?? '') || collate(a.first_name, b.first_name)
  }

  return {
    // onUnauthorized sets the one listener told when a request comes back 401
    // (null to stop). Sign-in requests (/auth/…) never call it.
    onUnauthorized(listener: (() => void) | null): void {
      unauthorized = listener
    },

    async login(token: string): Promise<void> {
      await send<void>('POST', '/auth/v1/login', { token })
    },

    // Emails a link and a 6-digit code if a professional has this address.
    // The answer is the same either way, so it says nothing about accounts.
    async requestLoginEmail(email: string): Promise<void> {
      await send<unknown>('POST', '/auth/v1/login-link', { email })
    },

    async loginWithCode(email: string, code: string): Promise<void> {
      await send<void>('POST', '/auth/v1/login', { email, code })
    },

    me(): Promise<Me> {
      return get<Me>('/v1/me')
    },

    async getToday(): Promise<TodayScreen> {
      const screen = await get<TodayScreen>('/v1/today')
      return { ...screen, appointments: screen.appointments.map(normalizeAppointment) }
    },

    // What she did with one of Today's cards: tapped its action, or snoozed
    // it. The AI acceptance KPI is measured at this tap; the API logs it by
    // the card's key and answers nothing.
    async reportPriorityOutcome(key: string, outcome: 'acted' | 'snoozed'): Promise<void> {
      await send<void>('POST', '/v1/today/outcomes', { key, outcome })
    },

    // Her notification list, newest first.
    async listNotifications(): Promise<AppNotification[]> {
      return (await get<{ items: AppNotification[] }>('/v1/notifications')).items
    },
    // How many are new since she last opened the list (the API stops at 10).
    async unreadNotifications(): Promise<number> {
      return (await get<{ count: number }>('/v1/notifications/unread')).count
    },
    // She opened the list: everything in it is now seen.
    async markNotificationsSeen(): Promise<void> {
      await send<void>('POST', '/v1/notifications/seen', {})
    },

    // startCheckout starts a rung: follow url to Stripe Checkout, or, when
    // updated, the change was made in place. 409 already-on-rung or
    // billing-not-configured; 422 on a rung or interval it can't sell.
    startCheckout(input: { rung: PaidRung; interval?: BillingInterval }): Promise<CheckoutResult> {
      return send<CheckoutResult>('POST', '/v1/billing/checkout', input)
    },

    // cancelSubscription ends her paid rung at period end, or with
    // card_payments moves Pro down to Payments now (Stripe credits the
    // unused Pro days).
    async cancelSubscription(downgradeTo?: 'card_payments'): Promise<void> {
      await send<void>('POST', '/v1/billing/cancel', downgradeTo ? { downgrade_to: downgradeTo } : {})
    },

    // resumeSubscription takes back a cancellation at period end (Keep Pro,
    // Keep card payments on).
    async resumeSubscription(): Promise<void> {
      await send<void>('POST', '/v1/billing/cancel', { resume: true })
    },

    // portalUrl is a Stripe Customer Portal session for her card and invoices.
    async portalUrl(): Promise<string> {
      const { url } = await send<{ url: string }>('POST', '/v1/billing/portal')
      return url
    },

    // A rung's sheet was opened from a lock. Reported in the background,
    // like reportPriorityOutcome: nothing waits on it.
    async reportPromptTap(lock: PromptLock, rung: PaidRung): Promise<void> {
      await send<void>('POST', '/v1/billing/prompts', { lock, rung })
    },

    // Whether a Coach line or the briefing helped her, and why not. The API
    // keeps one verdict per key and answers nothing; helpful carries no reason.
    async reportVerdict(key: string, verdict: SuggestionVerdict, reason?: VerdictReason): Promise<void> {
      await send<void>('POST', '/v1/suggestions/verdicts', { key, verdict, reason: reason ?? '' })
    },

    async logout(): Promise<void> {
      await send<void>('POST', '/auth/v1/logout')
    },

    // Before she has named her business the API answers {"business": null}
    // (an API from before 2026-10-14 answered 404).
    async getBusiness(): Promise<Business | null> {
      try {
        const b = await get<Business | { business: null }>('/v1/business')
        if ('business' in b && b.business === null) return null
        return normalizeBusiness(b as Business)
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null
        }
        throw error
      }
    },

    // version is the business's version when the form was filled (If-Match):
    // a 412 VersionMismatchError means another device saved first. Without
    // it the save always goes through.
    updateBusiness(patch: BusinessPatch, version?: number): Promise<Business> {
      return send<Business>('PATCH', '/v1/business', patch, maybeIfMatch(rebase('business', version)))
        .then(normalizeBusiness)
        .then(remember('business'))
    },

    // A booking-page photo goes up as its own bytes (a JPEG the app has
    // already resized), not JSON.
    async uploadPhoto(kind: PhotoKind, photo: Blob): Promise<Business> {
      const res = await request(`/v1/business/photos/${kind}`, {
        method: 'PUT',
        headers: { 'Content-Type': photo.type || 'image/jpeg' },
        body: photo,
      })
      return remember<Business>('business')(normalizeBusiness(await res.json()))
    },

    async removePhoto(kind: PhotoKind): Promise<Business> {
      return send<Business>('DELETE', `/v1/business/photos/${kind}`).then(normalizeBusiness).then(remember('business'))
    },

    // --- Consultation forms ---

    async listFormTemplates(): Promise<FormTemplate[]> {
      return (await get<{ templates: FormTemplate[] }>('/v1/form-templates')).templates
    },

    async listFormStarters(): Promise<FormStarter[]> {
      return (await get<{ starters: FormStarter[] }>('/v1/form-starters')).starters
    },

    createFormTemplate(input: FormTemplateInput): Promise<FormTemplate> {
      return send<FormTemplate>('POST', '/v1/form-templates', input)
    },

    getFormTemplate(templateId: string): Promise<FormTemplate> {
      return get<FormTemplate>(`/v1/form-templates/${id(templateId)}`)
    },

    // Saving publishes a new version; earlier signatures keep the old one.
    updateFormTemplate(templateId: string, input: FormTemplateInput): Promise<FormTemplate> {
      return send<FormTemplate>('PUT', `/v1/form-templates/${id(templateId)}`, input)
    },

    // 409 while a live service still uses it.
    async archiveFormTemplate(templateId: string): Promise<void> {
      await send<void>('POST', `/v1/form-templates/${id(templateId)}/archive`)
    },

    // Send form, and Ask again: emails the client their link.
    async sendClientForm(clientId: string, templateId: string): Promise<void> {
      await send<void>('POST', `/v1/clients/${id(clientId)}/forms/${id(templateId)}/send`)
    },

    // Stop asking: closes the client's open request.
    async stopClientForm(clientId: string, templateId: string): Promise<void> {
      await send<void>('POST', `/v1/clients/${id(clientId)}/forms/${id(templateId)}/stop`)
    },

    async listClientForms(clientId: string): Promise<ClientForm[]> {
      return (await get<{ forms: ClientForm[] }>(`/v1/clients/${id(clientId)}/forms`)).forms
    },

    getFormSubmission(submissionId: string): Promise<FormSubmission> {
      return get<FormSubmission>(`/v1/form-submissions/${id(submissionId)}`)
    },

    // The drawn signature's image, for an <img src>. It is served to her
    // alone: the browser sends her session cookie, and it is never cached.
    formSignatureUrl(submissionId: string): string {
      return `${base}/v1/form-submissions/${id(submissionId)}/signature`
    },

    async listServices(): Promise<Service[]> {
      return (await get<{ services: Service[] }>('/v1/services')).services.map(normalizeService)
    },

    createService(input: ServiceInput): Promise<Service> {
      return send<Service>('POST', '/v1/services', input).then(normalizeService)
    },

    updateService(serviceId: string, input: ServiceInput, version?: number): Promise<Service> {
      return send<Service>('PATCH', `/v1/services/${id(serviceId)}`, input, maybeIfMatch(rebase(serviceId, version)))
        .then(normalizeService)
        .then(remember(serviceId))
    },

    async deleteService(serviceId: string): Promise<void> {
      await send<void>('DELETE', `/v1/services/${id(serviceId)}`)
    },

    async reorderServices(ids: string[]): Promise<void> {
      await send<void>('POST', '/v1/services/reorder', { ids })
    },

    async getWorkingHours(): Promise<OpeningWindow[]> {
      return (await get<{ windows: OpeningWindow[] }>('/v1/working-hours')).windows
    },

    async putWorkingHours(windows: OpeningWindow[]): Promise<OpeningWindow[]> {
      return (await send<{ windows: OpeningWindow[] }>('PUT', '/v1/working-hours', { windows })).windows
    },

    async getPlan(): Promise<Plan> {
      const plan = await get<Plan>('/v1/plan')
      return { ...plan, goal: normalizePlanGoal(plan.goal) }
    },

    async putPlan(input: PlanInput): Promise<Plan> {
      const plan = await send<Plan>('PUT', '/v1/plan', input)
      return { ...plan, goal: normalizePlanGoal(plan.goal) }
    },

    async applyPlan(force: boolean): Promise<OpeningWindow[]> {
      return (await send<{ windows: OpeningWindow[] }>('POST', '/v1/plan/apply', { force })).windows
    },

    getCoach(): Promise<Coach> {
      return get<Coach>('/v1/coach')
    },

    async listTimeOff(): Promise<TimeOff[]> {
      return (await get<{ time_off: TimeOff[] }>('/v1/time-off')).time_off
    },

    addTimeOff(input: TimeOffInput): Promise<TimeOff> {
      return send<TimeOff>('POST', '/v1/time-off', input)
    },

    async deleteTimeOff(timeOffId: string): Promise<void> {
      await send<void>('DELETE', `/v1/time-off/${id(timeOffId)}`)
    },

    // An API from before time blocks answers 404: no blocks, so the calendar
    // still loads while a deploy rolls out.
    async listTimeBlocks(from: string, to: string): Promise<TimeBlock[]> {
      try {
        return (await get<{ time_blocks: TimeBlock[] }>(`/v1/time-blocks?from=${id(from)}&to=${id(to)}`)).time_blocks
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return []
        }
        throw error
      }
    },

    // 409 appointment-conflict (a ConflictError) when the block overlaps a
    // booking or another block and force isn't set.
    createTimeBlock(input: TimeBlockInput): Promise<TimeBlock> {
      return send<TimeBlock>('POST', '/v1/time-blocks', input)
    },

    updateTimeBlock(timeBlockId: string, input: TimeBlockInput): Promise<TimeBlock> {
      return send<TimeBlock>('PATCH', `/v1/time-blocks/${id(timeBlockId)}`, input)
    },

    async deleteTimeBlock(timeBlockId: string): Promise<void> {
      await send<void>('DELETE', `/v1/time-blocks/${id(timeBlockId)}`)
    },

    async listAppointments(from: string, to: string): Promise<Appointment[]> {
      const query = `from=${id(from)}&to=${id(to)}`
      return (await get<{ appointments: Appointment[] }>(`/v1/appointments?${query}`)).appointments.map(normalizeAppointment)
    },

    async listReviews(): Promise<Review[]> {
      return (await get<{ reviews: Review[] }>('/v1/reviews')).reviews
    },
    async clientReviews(clientId: string): Promise<Review[]> {
      return (await get<{ reviews: Review[] }>(`/v1/clients/${id(clientId)}/reviews`)).reviews
    },
    importReview(input: ImportReviewInput): Promise<Review> {
      return send<Review>('POST', '/v1/reviews', input)
    },
    updateReview(reviewId: string, patch: ReviewPatch): Promise<Review> {
      return send<Review>('PATCH', `/v1/reviews/${id(reviewId)}`, patch)
    },
    deleteReview(reviewId: string): Promise<void> {
      return send<void>('DELETE', `/v1/reviews/${id(reviewId)}`)
    },
    appointmentMail(appointmentId: string): Promise<{ emails: AppointmentEmail[]; review: Review | null }> {
      return get<{ emails: AppointmentEmail[]; review: Review | null }>(`/v1/appointments/${id(appointmentId)}/emails`)
    },
    async appointmentEmails(appointmentId: string): Promise<AppointmentEmail[]> {
      return (await get<{ emails: AppointmentEmail[] }>(`/v1/appointments/${id(appointmentId)}/emails`)).emails
    },
    getAppointment(appointmentId: string): Promise<Appointment> {
      return get<Appointment>(`/v1/appointments/${id(appointmentId)}`).then(normalizeAppointment)
    },

    createAppointment(input: AppointmentInput): Promise<Appointment> {
      return send<Appointment>('POST', '/v1/appointments', input).then(normalizeAppointment)
    },

    changeAppointment(appointmentId: string, version: number, patch: AppointmentPatch): Promise<Appointment> {
      return send<Appointment>('PATCH', `/v1/appointments/${id(appointmentId)}`, patch, ifMatch(version)).then(normalizeAppointment)
    },

    // setDiscount puts a one-off discount or a code on an appointment;
    // clearDiscount takes it off. 422 discount-code / discount-below-paid,
    // 409 appointment-state.
    setDiscount(appointmentId: string, version: number, body: DiscountRequest): Promise<Appointment> {
      return send<Appointment>('PUT', `/v1/appointments/${id(appointmentId)}/discount`, body, ifMatch(version)).then(normalizeAppointment)
    },

    clearDiscount(appointmentId: string, version: number): Promise<Appointment> {
      return send<Appointment>('DELETE', `/v1/appointments/${id(appointmentId)}/discount`, undefined, ifMatch(version)).then(normalizeAppointment)
    },

    // Inside her cancellation notice the deposit is kept (deposit_on_cancel
    // "keep") unless refundDeposit is true.
    cancelAppointment(appointmentId: string, version: number, reason: string | null, refundDeposit?: boolean): Promise<Appointment> {
      const body = refundDeposit === undefined ? { reason } : { reason, refund_deposit: refundDeposit }
      return send<Appointment>('POST', `/v1/appointments/${id(appointmentId)}/cancel`, body, ifMatch(version)).then(normalizeAppointment)
    },

    // markNoShow keeps any deposit and emails nobody. 409 appointment-not-started
    // for an appointment after today.
    markNoShow(appointmentId: string, version: number): Promise<Appointment> {
      return send<Appointment>('POST', `/v1/appointments/${id(appointmentId)}/no-show`, undefined, ifMatch(version)).then(normalizeAppointment)
    },

    // undoNoShow puts it back to booked; 409 appointment-state once money moved since.
    undoNoShow(appointmentId: string, version: number): Promise<Appointment> {
      return send<Appointment>('POST', `/v1/appointments/${id(appointmentId)}/no-show/undo`, undefined, ifMatch(version)).then(normalizeAppointment)
    },

    // linkPackage puts the client's package on a booked appointment ("Use their
    // package"); unlinkPackage takes it off ("Don't use the package"). 422 on
    // "package" when she has no covering session or has paid towards it;
    // 409 appointment-state once completed or cancelled.
    linkPackage(appointmentId: string, version: number): Promise<Appointment> {
      return send<Appointment>('POST', `/v1/appointments/${id(appointmentId)}/package`, undefined, ifMatch(version)).then(normalizeAppointment)
    },

    unlinkPackage(appointmentId: string, version: number): Promise<Appointment> {
      return send<Appointment>('DELETE', `/v1/appointments/${id(appointmentId)}/package`, undefined, ifMatch(version)).then(normalizeAppointment)
    },

    // releaseHold gives back the time of a booking still waiting for its
    // deposit. A 409 appointment-state means it was paid (or went) meanwhile.
    releaseHold(appointmentId: string): Promise<Appointment> {
      return send<Appointment>('POST', `/v1/appointments/${id(appointmentId)}/release`).then(normalizeAppointment)
    },

    // requestDeposit emails the client a link to pay this booking's deposit.
    // 409 no-client-email / no-deposit-rule, 422 nothing-to-charge.
    requestDeposit(appointmentId: string): Promise<Appointment> {
      return send<Appointment>('POST', `/v1/appointments/${id(appointmentId)}/deposit-request`).then(normalizeAppointment)
    },

    // paid may be left out when amount_due_pence is 0: the server settles it
    // with the package session it was booked with, or with nothing.
    completeAppointment(
      appointmentId: string,
      version: number,
      paid?: PaymentMethod | 'not_yet' | 'package',
      idempotencyKey?: string,
    ): Promise<Appointment> {
      const headers = { ...ifMatch(version), ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}) }
      return send<Appointment>('POST', `/v1/appointments/${id(appointmentId)}/complete`, paid === undefined ? {} : { paid }, headers).then(normalizeAppointment)
    },

    // recordPayment settles an already-completed appointment that was left
    // unpaid, with whichever method she actually collected it by.
    recordPayment(appointmentId: string, method: PaymentMethod, idempotencyKey?: string): Promise<Appointment> {
      return send<Appointment>('POST', `/v1/appointments/${id(appointmentId)}/payments`, { method }, idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}).then(normalizeAppointment)
    },

    cardAccount(): Promise<CardAccount> {
      return get<CardAccount>('/v1/payments/account').then(normalizeCardAccount)
    },

    startCardAccount(): Promise<CardAccount> {
      return send<CardAccount>('POST', '/v1/payments/account').then(normalizeCardAccount)
    },

    // cardAccountLink returns a single-use link to Stripe's onboarding form.
    async cardAccountLink(): Promise<string> {
      return (await send<{ url: string }>('POST', '/v1/payments/account/link')).url
    },

    // cardDashboardLink returns a single-use link that signs her in to her
    // Express Dashboard on Stripe. There is one only once Stripe approves her.
    async cardDashboardLink(): Promise<string> {
      return (await send<{ url: string }>('POST', '/v1/payments/account/dashboard')).url
    },

    // cardAccountSession returns the client secret the embedded components use.
    async cardAccountSession(): Promise<string> {
      return (await send<{ client_secret: string }>('POST', '/v1/payments/session')).client_secret
    },

    // The server works out the amount; the client only says what to charge for.
    // complete: the link is made from her Complete step, and the server
    // completes a booked visit (unpaid) first, as a charge with complete does.
    createCheckout(target: { appointment_id: string; complete?: boolean } | { client_id: string; package_id: string }, idempotencyKey?: string, saveCard?: boolean): Promise<CardCheckout> {
      // Left out, an open link keeps whether it saves the card; true or false sets it.
      return send<CardCheckout>('POST', '/v1/payments/checkouts', saveCard === undefined ? target : { ...target, save_card: saveCard }, idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {})
    },

    // Charging a saved card. The key is made once per confirm sheet and reused
    // for every retry, so a double tap cannot charge twice. The amount is the
    // one the sheet showed: the server refuses (not-chargeable) when the
    // amount due now differs.
    charge(target: { appointment_id: string; complete?: boolean } | { client_id: string; package_id: string }, idempotencyKey: string, expectedAmountPence: number): Promise<CardCharge> {
      return send<CardCharge>('POST', '/v1/payments/charges', { ...target, expected_amount_pence: expectedAmountPence }, { 'Idempotency-Key': idempotencyKey })
    },

    getCharge(chargeId: string): Promise<CardCharge> {
      return get<CardCharge>(`/v1/payments/charges/${id(chargeId)}`)
    },

    removeCard(clientId: string): Promise<void> {
      return send<void>('DELETE', `/v1/clients/${id(clientId)}/card`)
    },

    getCheckout(checkoutId: string): Promise<CardCheckout> {
      return get<CardCheckout>(`/v1/payments/checkouts/${id(checkoutId)}`)
    },

    cancelCheckout(checkoutId: string): Promise<CardCheckout> {
      return send<CardCheckout>('DELETE', `/v1/payments/checkouts/${id(checkoutId)}`)
    },

    refund(
      paymentId: string,
      body: { amount_pence: number; remove_credits: number; reason: string | null },
      idempotencyKey: string,
    ): Promise<RefundResult> {
      return send<RefundResult>('POST', `/v1/payments/${id(paymentId)}/refunds`, body, { 'Idempotency-Key': idempotencyKey })
    },

    async searchClients(query: string): Promise<ClientSummary[]> {
      const clients = (await get<{ clients: ClientSummary[] }>(`/v1/clients?q=${id(query)}`)).clients
      // The list endpoint now orders by id (creation order); the booking sheet
      // still expects alphabetical results, so sort here by last name then
      // first name, case-insensitively.
      return [...clients].sort(compareClients)
    },

    createClient(input: ClientInput): Promise<ClientSummary> {
      return send<ClientSummary>('POST', '/v1/clients', input)
    },

    async listClients(params: { q?: string; filter?: '' | 'new' | 'vip' | 'no_email'; cursor?: string } = {}): Promise<ClientPage> {
      const search = new URLSearchParams()
      if (params.q) search.set('q', params.q)
      if (params.filter) search.set('filter', params.filter)
      if (params.cursor) search.set('cursor', params.cursor)
      const query = search.toString()
      return get<ClientPage>(`/v1/clients${query ? `?${query}` : ''}`)
    },

    getClient(clientId: string): Promise<ClientDetail> {
      return get<ClientDetail>(`/v1/clients/${id(clientId)}`)
    },

    async listServiceCategories(): Promise<ServiceCategory[]> {
      return (await get<{ categories: ServiceCategory[] }>('/v1/service-categories')).categories
    },

    // createServiceCategory adds one of her own; 422 "label" when she already has that name.
    createServiceCategory(label: string): Promise<ServiceCategory> {
      return send<ServiceCategory>('POST', '/v1/service-categories', { label })
    },

    // deleteServiceCategory: 409 category-in-use while services are in it.
    async deleteServiceCategory(value: string): Promise<void> {
      await send<void>('DELETE', `/v1/service-categories/${id(value)}`)
    },

    updateClient(clientId: string, patch: ClientPatch, version?: number): Promise<ClientDetail> {
      return send<ClientDetail>('PATCH', `/v1/clients/${id(clientId)}`, patch, maybeIfMatch(rebase(clientId, version)))
        .then(remember(clientId))
    },

    // resendClientConfirmation emails the client a link to confirm their
    // email (202, no body). 429 too-many past 3 a day; 409 email-confirmed or
    // no-client-email.
    async resendClientConfirmation(clientId: string): Promise<void> {
      await request(`/v1/clients/${id(clientId)}/confirmation`, { method: 'POST' })
    },

    clientDetailsChecked(clientId: string): Promise<ClientDetail> {
      return send<ClientDetail>('POST', `/v1/clients/${id(clientId)}/details-checked`)
    },

    async eraseClient(clientId: string): Promise<void> {
      await send<void>('DELETE', `/v1/clients/${id(clientId)}`)
    },

    async acceptAgreements(docs: LegalDocument[]): Promise<void> {
      await send<void>('POST', '/v1/agreements', {
        accepted: docs.map((d) => ({ document: d.document, version: d.version })),
      })
    },

    // A document the app shows itself, by the path its url names (the
    // DPIA at /legal/dpia): the text she reads before adopting it.
    legalDocument(path: string): Promise<LegalText> {
      return get<LegalText>(`/v1${path}`)
    },

    // The privacy notice her clients read, built from her business details;
    // null while she has none saved.
    clientNotice(): Promise<ClientNoticeData | null> {
      return get<ClientNoticeData | null>('/v1/client-notice')
    },

    // She has read it and it looks right.
    async clientNoticeChecked(): Promise<void> {
      await send<void>('POST', '/v1/client-notice/checked')
    },

    closeAccount(): Promise<Closure> {
      return send<Closure>('POST', '/v1/account/close', { confirm: 'close my account' })
    },

    keepAccount(): Promise<Closure> {
      return send<Closure>('DELETE', '/v1/account/close')
    },

    getCalendarFeed(): Promise<{ enabled: boolean }> {
      return get<{ enabled: boolean }>('/v1/calendar-feed')
    },

    // Makes (or resets) her private calendar link; the URL is only ever
    // returned here.
    async createCalendarFeed(): Promise<string> {
      return (await send<{ url: string }>('POST', '/v1/calendar-feed')).url
    },

    turnOffCalendarFeed(): Promise<void> {
      return send<void>('DELETE', '/v1/calendar-feed')
    },

    getRetention(): Promise<RetentionReview> {
      return get<RetentionReview>('/v1/retention')
    },

    // Erases the confirmed clients still past her retention period, and
    // returns how many went: a client who rebooked since is skipped.
    async eraseExpiredRecords(clientIds: string[]): Promise<number> {
      return (await send<{ erased: number }>('POST', '/v1/retention/erase', { client_ids: clientIds })).erased
    },

    getTimeline(clientId: string, cursor?: string, includeCancelled = false): Promise<TimelinePage> {
      const search = new URLSearchParams()
      if (cursor) search.set('cursor', cursor)
      if (includeCancelled) search.set('include_cancelled', '1')
      const query = search.toString()
      return get<TimelinePage>(`/v1/clients/${id(clientId)}/timeline${query ? `?${query}` : ''}`)
    },

    async getPreferences(clientId: string): Promise<Preference[]> {
      return (await get<{ preferences: Preference[] }>(`/v1/clients/${id(clientId)}/preferences`)).preferences
    },

    async putPreferences(clientId: string, list: PreferenceInput[]): Promise<Preference[]> {
      return (await send<{ preferences: Preference[] }>('PUT', `/v1/clients/${id(clientId)}/preferences`, { preferences: list })).preferences
    },

    async listNotes(clientId: string): Promise<Note[]> {
      return (await get<{ notes: Note[] }>(`/v1/clients/${id(clientId)}/notes`)).notes
    },

    createNote(clientId: string, input: NoteInput): Promise<Note> {
      return send<Note>('POST', `/v1/clients/${id(clientId)}/notes`, input)
    },

    updateNote(clientId: string, noteId: string, etag: string, input: NoteInput): Promise<Note> {
      return send<Note>('PATCH', `/v1/clients/${id(clientId)}/notes/${id(noteId)}`, input, { 'If-Match': `"${etag}"` })
    },

    // A treatment photo goes up as its own bytes (already resized, with its
    // size in the query so the API need not decode it to know). 409
    // photo-consent-needed / photo-limit, 503 photos-unavailable, 422 on
    // field photo for a file it will not take. The caller keeps one
    // idempotencyKey per photo across retries, so a retry after a lost answer
    // gets the first upload back rather than a second copy.
    async uploadNotePhoto(clientId: string, noteId: string, photo: { blob: Blob; width: number; height: number }, idempotencyKey: string): Promise<NotePhoto> {
      const res = await request(`/v1/clients/${id(clientId)}/notes/${id(noteId)}/photos?w=${photo.width}&h=${photo.height}`, {
        method: 'POST',
        headers: { 'Content-Type': photo.blob.type || 'image/jpeg', 'Idempotency-Key': idempotencyKey },
        body: photo.blob,
      })
      return res.json()
    },

    async notePhotoBlob(clientId: string, noteId: string, photoId: string): Promise<Blob> {
      const res = await request(`/v1/clients/${id(clientId)}/notes/${id(noteId)}/photos/${id(photoId)}`)
      return res.blob()
    },

    async removeNotePhoto(clientId: string, noteId: string, photoId: string): Promise<void> {
      await send<void>('DELETE', `/v1/clients/${id(clientId)}/notes/${id(noteId)}/photos/${id(photoId)}`)
    },

    recordConsent(clientId: string, input: ConsentInput): Promise<Consent> {
      return send<Consent>('POST', `/v1/clients/${id(clientId)}/consent`, input)
    },

    async getSlots(serviceLengthId: string, date: string, exclude?: string): Promise<number[]> {
      let query = `service_length_id=${id(serviceLengthId)}&date=${id(date)}`
      if (exclude) {
        query += `&exclude=${id(exclude)}`
      }
      return (await get<{ starts: number[] }>(`/v1/slots?${query}`)).starts
    },

    async listPackages(): Promise<Package[]> {
      return (await get<{ packages: Package[] }>('/v1/packages')).packages
    },

    createPackage(input: PackageInput): Promise<Package> {
      return send<Package>('POST', '/v1/packages', input)
    },

    updatePackage(packageId: string, input: PackageInput, version?: number): Promise<Package> {
      return send<Package>('PATCH', `/v1/packages/${id(packageId)}`, input, maybeIfMatch(rebase(packageId, version)))
        .then(remember(packageId))
    },

    async deletePackage(packageId: string): Promise<void> {
      await send<void>('DELETE', `/v1/packages/${id(packageId)}`)
    },

    async listDiscounts(): Promise<Discount[]> {
      return (await get<{ discounts: Discount[] }>('/v1/discounts')).discounts
    },

    createDiscount(input: DiscountInput): Promise<Discount> {
      return send<Discount>('POST', '/v1/discounts', input)
    },

    updateDiscount(discountId: string, input: DiscountInput): Promise<Discount> {
      return send<Discount>('PUT', `/v1/discounts/${id(discountId)}`, input)
    },

    async endDiscount(discountId: string): Promise<void> {
      await send<unknown>('POST', `/v1/discounts/${id(discountId)}/end`)
    },

    async deleteDiscount(discountId: string): Promise<void> {
      await send<void>('DELETE', `/v1/discounts/${id(discountId)}`)
    },

    async clientPackages(clientId: string): Promise<Purchase[]> {
      return (await get<{ packages: Purchase[] }>(`/v1/clients/${id(clientId)}/packages`)).packages.map(normalizePurchase)
    },

    // method is how the client paid for the package.
    sellPackage(clientId: string, packageId: string, method: PaymentMethod, idempotencyKey?: string): Promise<Purchase> {
      return send<Purchase>('POST', `/v1/clients/${id(clientId)}/packages`, {
        package_id: packageId,
        method,
      }, idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}).then(normalizePurchase)
    },

    // sellCarriedOverPackage adds a package bought elsewhere with sessionsLeft
    // still to use. Nothing is paid here, so there is no method.
    sellCarriedOverPackage(clientId: string, packageId: string, sessionsLeft: number, idempotencyKey?: string): Promise<Purchase> {
      return send<Purchase>('POST', `/v1/clients/${id(clientId)}/packages`, {
        package_id: packageId,
        carried_over: { sessions_left: sessionsLeft },
      }, idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}).then(normalizePurchase)
    },

    getEarnings(month?: string): Promise<Earnings> {
      return get<Earnings>(`/v1/earnings${month ? `?month=${id(month)}` : ''}`).then(normalizeEarnings)
    },

    createExpense(input: ExpenseInput): Promise<Expense> {
      return send<Expense>('POST', '/v1/expenses', input)
    },

    updateExpense(expenseId: string, input: ExpenseInput): Promise<Expense> {
      return send<Expense>('PUT', `/v1/expenses/${id(expenseId)}`, input)
    },

    async deleteExpense(expenseId: string): Promise<void> {
      await send<void>('DELETE', `/v1/expenses/${id(expenseId)}`)
    },

    // exportUrl is a downloadable link for one of the export endpoints: the
    // session cookie the browser sends itself is enough to authorise it, so
    // this is just base + path rather than a fetch.
    exportUrl(path: string): string {
      return `${base}${path}`
    },
  }
}

export type Api = ReturnType<typeof createApi>

export const api = createApi(API_BASE, undefined, { build: APP_BUILD })
